import React from "react";
import { PageBuilderFycFragment } from "types/graphql-types";
import { useFYC } from "components/FindYourCruise/useFYC";
import FindYourCruise from "components/FindYourCruise";
import FYCQueryBuilder from "../FindYourCruise/FYCQueryBuilder";
import { graphql } from "gatsby";
import { stringPresent } from "src/utils/strings";
import { useRemoteOfferByIds } from "../../utils/useRemoteOffers";
import { WindowLocation } from "@reach/router";
import { CruisePerimeter } from "types/api/shared";
import { UseFetchDataResponse } from "../../utils/useFetchApi";
import { OfferFromApi } from "types/api/exclusive_offers";
import { isPresent } from "src/utils/checks";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

function parseCruiseCodesToCruiseCodesArray(cruiseCodes: string): string[] {
  return cruiseCodes
    .split(/[\n,]+/) //split by comma or new line
    .map((c) => c.trim()) //removes white spaces
    .filter((c) => c && c != ""); //filter empty results
}

const mergeCruisePerimeters = (
  remoteOffers: UseFetchDataResponse<OfferFromApi[]>
): CruisePerimeter =>
  (remoteOffers.data || [])
    .map((x) => x.cruisePerimeter)
    .reduce(
      (status, item) => {
        if (status.type === "AllCruises") return status;
        if (item.type === "AllCruises") return item;
        return {
          cruiseCodes: [...status.cruiseCodes, ...item.cruiseCodes],
          type: "SomeCruises"
        };
      },
      {
        type: "SomeCruises",
        cruiseCodes: []
      } as CruisePerimeter
    );

const PageBuilderFyc: React.FC<
  PageBuilderFycFragment & { location: WindowLocation }
> = ({
  title,
  showDestinationFilter = false,
  showShipFilter = false,
  showExpeditionFilter = false,
  showPromosFilter = false,
  filterByCruiseType,
  filterByComboType,
  filterByCruise,
  filterByCruiseCodes,
  filterByDestination,
  filterByOffer,
  filterByShip,
  filterByPorts,
  filterByCountries,
  location
}) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const fycQueryBuilder = new FYCQueryBuilder();

  if (filterByCruiseType?.length)
    fycQueryBuilder.filterByCruiseTypes(filterByCruiseType.filter(isPresent));

  if (filterByComboType?.length)
    fycQueryBuilder.filterByComboTypes(filterByComboType.filter(isPresent));

  if (filterByCruise?.length)
    fycQueryBuilder.filterByCruiseCodes(
      filterByCruise.map((c) => c?.data?.cruiseCode).filter(isPresent)
    );

  const remoteOffers = useRemoteOfferByIds(
    filterByOffer?.map((x) => x?._id).filter(isPresent) || [],
    location,
    language
  );

  if (remoteOffers.status === "success") {
    const totalPerimeter = mergeCruisePerimeters(remoteOffers);
    if (
      totalPerimeter.type === "SomeCruises" &&
      totalPerimeter.cruiseCodes.length > 0
    )
      fycQueryBuilder.filterByCruiseCodes(totalPerimeter.cruiseCodes);
  }

  if (stringPresent(filterByCruiseCodes)) {
    const codes = parseCruiseCodesToCruiseCodesArray(filterByCruiseCodes);
    fycQueryBuilder.filterByCruiseCodes(codes);
  }

  if (filterByDestination?.length)
    fycQueryBuilder.filterByDestinationIds(
      filterByDestination.map((d) => d?.destinationId).filter(isPresent)
    );

  if (filterByShip?.length)
    fycQueryBuilder.filterByShipsName(
      filterByShip.map((s) => s?.name).filter(isPresent)
    );

  if (filterByPorts?.length) {
    const portCodes = filterByPorts.map((p) => p?.data?.code).filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  if (filterByCountries?.length) {
    const portCodes = filterByCountries
      .flatMap((country) => country?.ports?.map((p) => p?.data?.code))
      .filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  const {
    searchClient,
    filterDefaultSearch,
    searchState,
    createURL,
    onSearchStateChange,
    localizedSearchIndexName
  } = useFYC(fycQueryBuilder.generate(), geolocation.defaultCurrency);

  const hasTitle = title && title != "";
  return (
    <React.Fragment>
      {remoteOffers.status === "success" && (
        <FindYourCruise
          title={title?.localized}
          showTitle={hasTitle}
          titleAlign="center"
          backgroundColor="neutral10"
          searchClient={searchClient}
          initialSearchQuery={filterDefaultSearch}
          resultsPageSize={8}
          defaultSearchIndexName={localizedSearchIndexName}
          searchState={searchState}
          onSearchStateChange={onSearchStateChange}
          createURL={createURL}
          showFreeTextSearch={true}
          showExpeditionFilter={showExpeditionFilter}
          specialTypeFilter={undefined}
          showDestinationFilter={showDestinationFilter}
          showShipFilter={showShipFilter}
          showPromosFilter={showPromosFilter}
          showDurationFilter={true}
          showOthersFilter={true}
        />
      )}
    </React.Fragment>
  );
};

export default PageBuilderFyc;

export const fragments = graphql`
  fragment PageBuilderFyc on SanityFyc {
    _key
    title {
      localized
    }
    showDestinationFilter
    showShipFilter
    showExpeditionFilter
    showPromosFilter

    filterByShip {
      name
    }

    filterByDestination {
      destinationId
    }

    filterByOffer {
      ... on SanityExclusiveOfferPerAirPromo {
        _id
      }
      ... on SanityExclusiveOfferPerPromo {
        _id
      }
    }

    filterByCruiseType
    filterByComboType

    filterByCruise {
      ... on SanityCruise {
        data {
          cruiseCode
        }
      }

      ... on SanitySpecialVoyage {
        data {
          cruiseCode
        }
      }
    }

    filterByCruiseCodes

    filterByCountries {
      ports {
        data {
          code
        }
      }
    }

    filterByPorts {
      data {
        code
      }
    }
  }
`;
